import { getDeviceDetails } from 'core-lib/utils/device';
import { getEffectivePlatform, getPlatform } from 'core-lib/utils/helpers';
import {
  getInitialRoute,
  getNativeVersions,
  getPrimarySource,
  getRefererSource,
  isParkPlusWebView,
} from 'core-lib/utils/source';
import dayjs from 'dayjs';
import { LocalStorage, SessionStorage } from '../../constants/storage';
import { isServer } from '../utils';
import { getOrSetStorage, getQueryObj } from 'core-lib/utils/common';

const TRACKING_ATTR_ALIASES: Array<[string, string]> = [
  ['phone_number', 'mobile_number'],
  ['subcategory', 'sub_category'],
  ['timestamp', 'time_stamp'],
  ['selected_option', 'option_selected'],
];

export function normalizeTrackingAttributeAliases(
  attributes: Record<string, any> | null | undefined,
): Record<string, any> {
  if (!attributes || typeof attributes !== 'object') {
    return attributes || {};
  }

  const attr = attributes;

  for (const [legacyKey, canonicalKey] of TRACKING_ATTR_ALIASES) {
    const hasLegacy = Object.prototype.hasOwnProperty.call(attr, legacyKey);
    const hasCanonical = Object.prototype.hasOwnProperty.call(attr, canonicalKey);

    if (hasLegacy && !hasCanonical) {
      attr[canonicalKey] = attr[legacyKey];
    } else if (hasCanonical && !hasLegacy) {
      attr[legacyKey] = attr[canonicalKey];
    }
  }

  return attr;
}

export function getDefaultAttributes() {
  if (isServer()) return;

  const query = getQueryObj();

  const getEventParam = suffix => {
    const key = Object.keys(query || {}).find(k => k.endsWith(suffix));
    return key ? query[key] : null;
  };

  const utmSource = getOrSetStorage({
    key: SessionStorage.utmSource,
    queryValue: query?.utm_source,
  });
  const utmCampaign = getOrSetStorage({
    key: SessionStorage.utmCampaign,
    queryValue: query?.utm_campaign,
  });
  const utmContent = getOrSetStorage({
    key: SessionStorage.utmContent,
    queryValue: query?.utm_content,
  });
  const utmTerm = getOrSetStorage({ key: SessionStorage.utmTerm, queryValue: query?.utm_term });
  const adGroupid = getOrSetStorage({
    key: SessionStorage.adGroupId,
    queryValue: getEventParam('adgroupid'),
  });
  const utmMedium = getOrSetStorage({
    key: SessionStorage.utmMedium,
    queryValue: query?.utm_medium,
  });
  const clid = getOrSetStorage({ key: SessionStorage.clid, queryValue: getEventParam('clid') });
  const adCampaignid = getOrSetStorage({
    key: SessionStorage.adCampaignid,
    queryValue: getEventParam('ad_campaignid'),
  });
  const adSource = getOrSetStorage({
    key: SessionStorage.adSource,
    queryValue: getEventParam('ad_source'),
  });
  const braid = getOrSetStorage({ key: SessionStorage.braid, queryValue: getEventParam('braid') });

  const { pathname, fullPathname } = getInitialRoute();
  const profile = JSON.parse(localStorage.getItem('profile'));
  const phoneNumber = profile?.phone_number;
  const eventTimestamp = dayjs().format('YYYY-MM-DD, dddd, hh:mm A');

  const attr: any = {
    user_id: profile?.id,
    phone_number: phoneNumber,
    mobile_number: phoneNumber,
    initial_path: pathname,
    initial_full_path: fullPathname,
    primary_source: getPrimarySource(),
    referer_source: getRefererSource(),
    platform: getPlatform(),
    effective_platform: getEffectivePlatform(),
    timestamp: eventTimestamp,
    time_stamp: eventTimestamp,
    page_url: window.location.href,
    page_name: window.location.href,
    user_anonymous_id: localStorage.getItem(LocalStorage.SegmentAnonymousId),
    project_name: localStorage.getItem(LocalStorage.Theme),
    user_session_id: sessionStorage.getItem(SessionStorage.TrackingSessionId),
    ad_group_id: adGroupid,
    utm_source: utmSource,
    utm_term: utmTerm,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    clid: clid,
    ad_campaignid: adCampaignid,
    ad_source: adSource,
    braid: braid,
  };

  if (isParkPlusWebView()) {
    const { osVersion, appVersion } = getNativeVersions();
    attr.app_version = appVersion;
    attr.os_version = osVersion;
  } else {
    const { os, browser } = getDeviceDetails();
    attr.os_version = os.version;
    attr.os_name = os.name;
    attr.browser_version = browser.version;
    attr.browser_name = browser.name;
  }

  return attr;
}
